import {
  computeInvoicePaymentMentions,
  computeParticularMentions,
  computeQuotePaymentMentions,
  InvoicePaymentMention,
  QuotePaymentMention,
} from '@shinetools/invoicing-rules';

import { InvoicingSpecialMentions } from '../../pages/hooks/useInvoicingSpecialMentionsQuery';
import {
  getLocalizedMentionsFromInvoice,
  getLocalizedMentionsFromQuote,
} from './helpers/getLocalizedMentions';
import { populateLocaleValues } from './helpers/populateLocaleValues';

type Client = {
  vatId: string;
  country: string;
  type: string | null;
};

type InvoicingData = {
  startAt: number;
  lateFeePercentage: number;
  lateMaxDays: number;
  client: Client | undefined;
  company: {
    isMicro: boolean;
    legalVATNumber: string;
    tradeName: string | null;
    legalName: string;
  };
  tasks: {
    totalAmountBeforeTaxes: number;
    totalVatAmount: number;
    vatPercentage: number;
    unitPrice: number;
    units: number;
  }[];
};

const mapType = (
  type: string | null | undefined,
): 'PRIVATE_INDIVIDUAL' | 'ORGANIZATION' | null => {
  if (type === 'PRIVATE_INDIVIDUAL') {
    return 'PRIVATE_INDIVIDUAL';
  }

  if (type === 'ORGANIZATION') {
    return 'ORGANIZATION';
  }

  return null;
};

const convertClient = (client: Client) => {
  const type = mapType(client?.type);
  return { ...client, type };
};

export const computeInvoiceSpecialMentions = (
  data: InvoicingData,
  invoicingSpecialMentions: InvoicingSpecialMentions | undefined,
) => {
  if (!invoicingSpecialMentions || !data.client) {
    return {
      particularMentions: [],
      paymentMentions: [],
    };
  }
  const particularMentionType = computeParticularMentions({
    client: convertClient(data.client),
    company: data.company,
    tasks: data.tasks,
  });

  const particularMentionTypeArray = particularMentionType
    ? [particularMentionType]
    : [];

  const paymentMentionType = computeInvoicePaymentMentions({
    clientType: convertClient(data.client).type,
  });

  const { particularMentions, paymentMentions: rawPaymentMentions } =
    getLocalizedMentionsFromInvoice(
      {
        particularMentions: particularMentionTypeArray,
        paymentMentions: [paymentMentionType],
      },
      invoicingSpecialMentions,
    );

  const paymentMentions = rawPaymentMentions.map((paymentMention) =>
    populateLocaleValues<InvoicePaymentMention>(
      data.company,
      data,
      paymentMention,
    ),
  );

  return {
    particularMentions,
    paymentMentions,
  };
};

export const computeQuoteSpecialMentions = (
  data: InvoicingData,
  invoicingSpecialMentions: InvoicingSpecialMentions | undefined,
) => {
  if (!invoicingSpecialMentions || !data.client) {
    return {
      particularMentions: [],
      paymentMentions: [],
    };
  }
  const particularMentionType = computeParticularMentions({
    client: convertClient(data.client),
    company: data.company,
    tasks: data.tasks,
  });

  const particularMentionTypeArray = particularMentionType
    ? [particularMentionType]
    : [];

  const paymentMentionType = computeQuotePaymentMentions({
    clientType: convertClient(data.client).type,
  });

  const { particularMentions, paymentMentions: rawPaymentMentions } =
    getLocalizedMentionsFromQuote(
      {
        particularMentions: particularMentionTypeArray,
        paymentMentions: [paymentMentionType],
      },
      invoicingSpecialMentions,
    );

  const paymentMentions = rawPaymentMentions.map((paymentMention) =>
    populateLocaleValues<QuotePaymentMention>(
      data.company,
      data,
      paymentMention,
    ),
  );

  return {
    particularMentions,
    paymentMentions,
  };
};
